// Loader.jsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const Loader = ({ setHasLoaded }) => {
  const greetings = [
    "नमस्ते",
    "Hello",
    "Hola",
    "Bonjour",
    "こんにちは",
    "Ciao",
    "안녕하세요",
    "Olá",
    "Hallo",
  ];

  const [currentWord, setCurrentWord] = useState(0);
  const [showLoader, setShowLoader] = useState(true);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % greetings.length);
    }, 650);

    const timeout = setTimeout(() => {
      setShowLoader(false);
      setHasLoaded(true);
    }, 3500);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [greetings.length, setHasLoaded]);

  return (
    <AnimatePresence>
      {showLoader && (
        <motion.div
          key="loader"
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="
            fixed inset-0 z-[9999] flex flex-col items-center justify-center 
            text-white select-none overflow-hidden
            bg-gradient-to-br from-[#0b1824] via-[#112b42] to-[#052b40]
            dark:from-black dark:via-[#0a0a0c] dark:to-black
          "
        >
          {/* Primary Ring */}
          <motion.div
            className="
              relative 
              w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 
              mb-8 flex items-center justify-center
            "
            animate={!reducedMotion && { rotate: 360 }}
            transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
          >
            {/* Outer precision ring */}
            <div className="absolute inset-0 rounded-full border-[4px] border-white/10 backdrop-blur-sm" />

            {/* Colored rotational arc */}
            <div className="absolute inset-0 rounded-full border-[6px] border-transparent border-t-cyan-400 border-r-blue-500 blur-[1px]" />

            {/* Tick marks on ring */}
            {[...Array(24)].map((_, i) => (
              <div
                key={i}
                className="absolute w-[3px] h-[10px] bg-white/20 origin-center rounded-full"
                style={{ transform: `rotate(${i * 15}deg) translateY(-85%)` }}
              />
            ))}

            {/* Micro pulse dot */}
            <motion.div
              className="absolute w-2 h-2 sm:w-2.5 sm:h-2.5 bg-cyan-400 rounded-full shadow-[0_0_6px_2px_rgba(0,198,255,0.7)]"
              animate={
                !reducedMotion && {
                  scale: [1, 1.4, 1],
                  opacity: [0.9, 1, 0.9],
                }
              }
              transition={{ duration: 1.4, repeat: Infinity }}
              style={{ transform: "translateY(-90%)" }}
            />

            {/* Inner depth plate */}
            <div className="absolute inset-[18%] sm:inset-[16%] md:inset-[14%] rounded-full bg-white/5 backdrop-blur-md shadow-inner"></div>
          </motion.div>

          {/* Rotating Text */}
          <motion.h1
            key={currentWord}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.45 }}
            className="
              text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 
              font-bold tracking-tight text-white
            "
          >
            {greetings[currentWord]}
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="
              mt-3 text-sm sm:text-base md:text-lg lg:text-xl 
              text-white/70 font-light tracking-wide
            "
          >
            Welcome To My Portfolio
          </motion.p>

          {/* Top / Bottom reflection gradients */}
          <div className="absolute top-0 w-full h-40 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 w-full h-40 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Loader;
